import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../stores/authStore'
import { MessageSquare, Users, Newspaper, BookOpen, Rss, LogIn, Info } from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import { Container, Section, SectionHeading, Breadcrumbs, Card, Button, Alert, Badge } from '../../components/portal/ui'

// Non-text meta for the feature grid (icon/path/auth). Text (title/desc) is
// zipped in from `pages.community.features` so it renders in the active language.
const FEATURES_META = [
  { icon: Rss, path: '/feed', auth: true },
  { icon: Users, path: '/groups', auth: true },
  { icon: MessageSquare, path: '/messages', auth: true },
  { icon: BookOpen, path: '/knowledge', auth: false },
  { icon: Newspaper, path: '/stories', auth: true },
  { icon: Info, path: '/search', auth: false },
]

export default function CommunityPage() {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuthStore()
  const features = t('pages.community.features', { returnObjects: true }) as { title: string; desc: string }[]

  return (
    <PortalLayout>
      <Seo
        title={t('pages.community.seo.title')}
        description={t('pages.community.seo.description')}
        canonicalPath="/community"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.communityPortal', 'Community'), path: '/community' }]}
      />
      <Section className="bg-gradient-mesh pb-6">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.communityPortal', 'Community') }]} />
          <SectionHeading as="h1" eyebrow={t('pages.community.hero.eyebrow')} title={t('pages.community.hero.title')} description={t('pages.community.hero.description')} />
          {!isAuthenticated && (
            <Alert tone="info" title={t('pages.community.guestAlert.title')}>
              {t('pages.community.guestAlert.body')}
            </Alert>
          )}
        </Container>
      </Section>

      <Section className="bg-white">
        <Container>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES_META.map((f, i) => {
              const Icon = f.icon
              const title = features[i]?.title ?? ''
              const desc = features[i]?.desc ?? ''
              return (
                <Card key={f.path} hover className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="w-11 h-11 rounded-xl bg-[rgb(232_241_252)] text-[rgb(15_76_129)] flex items-center justify-center" aria-hidden="true">
                      <Icon className="w-5 h-5" />
                    </span>
                    {f.auth && <Badge tone="amber">{t('common.loginNeeded')}</Badge>}
                  </div>
                  <h3 className="font-bold text-snow-900">{title}</h3>
                  <p className="text-sm text-snow-600 mt-1 mb-4">{desc}</p>
                  {f.auth && !isAuthenticated ? (
                    <Button to="/login" variant="primary" size="sm" icon={<LogIn className="w-4 h-4" />}>{t('pages.community.loginCta')}</Button>
                  ) : (
                    <Button to={f.path} variant="outline" size="sm">{t('pages.community.open')}</Button>
                  )}
                </Card>
              )
            })}
          </div>
        </Container>
      </Section>

      <Section className="bg-snow-50">
        <Container>
          <Card className="p-8 text-center bg-gradient-to-br from-[rgb(232_241_252)] to-white">
            <h2 className="text-xl font-bold text-snow-900">{t('pages.community.closing.title')}</h2>
            <p className="text-snow-600 mt-2 max-w-xl mx-auto">{t('pages.community.closing.body')}</p>
            <div className="mt-4 flex flex-wrap gap-3 justify-center">
              {!isAuthenticated && <Button to="/register" variant="primary">{t('pages.community.closing.ctaRegister')}</Button>}
              <Button to="/support-a-patient" variant="outline">{t('pages.community.closing.ctaSupport')}</Button>
              <Link to="/i-have-tb" className="btn-ghost">{t('pages.community.closing.ctaIHaveTb')}</Link>
            </div>
          </Card>
        </Container>
      </Section>
    </PortalLayout>
  )
}